import { useState, type ReactNode } from "react";
import type { BoutiqueCardDTO, PositionIndicatorDTO, ProductRankDTO } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { IosBar, IosGroup, IosRow, IosSection } from "@/components/mobile/MobileUi";
import { eur, margin, num, pct1 } from "@/lib/format";

/* Classements en lignes iOS, communs au mobile et au PC. */

/** Rang d'un classement (1, 2, 3…). */
function Rank({ n }: { n: number }) {
  return <span className="w-4 flex-shrink-0 text-[15px] font-semibold tabular text-muted">{n}</span>;
}

/** Top succursales intégrées / franchises : barre à l'échelle du CA max du périmètre. */
export function TopBoutiquesGroup({
  title,
  items,
  max,
  color,
  onSelect,
}: {
  title: string;
  items: BoutiqueCardDTO[];
  max: number;
  color: string;
  onSelect: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <IosSection title={title}>
      <IosGroup className="pb-[14px] pt-2">
        {items.map((b, i) => {
          const up = (b.delta ?? 0) >= 0;
          return (
            <IosRow
              key={b.id}
              onClick={() => onSelect(b.id)}
              className="min-h-[52px]"
              left={<Rank n={i + 1} />}
              right={
                <span className="flex-shrink-0 text-right">
                  <span className="block text-[15px] font-semibold tabular">{eur(b.ca)}</span>
                  <span className={cx("block text-[12px] font-semibold tabular", b.delta == null ? "text-faint" : up ? "text-positive" : "text-danger")}>
                    {b.delta == null ? "—" : `${up ? "▲" : "▼"} ${pct1(b.delta)}`}
                  </span>
                </span>
              }
            >
              <span className="block truncate text-[16px]">{b.city}</span>
              <IosBar pct={(b.ca / max) * 100} color={color} />
            </IosRow>
          );
        })}
      </IosGroup>
    </IosSection>
  );
}

/**
 * Top produits classés par CA, même présentation que le top boutiques ; toucher
 * → fiche produit. Pas de quantités : une partie du catalogue se vend au poids,
 * et des grammes additionnés à des unités ne classent rien.
 */
export function TopProductsGroup({
  title,
  products,
  ranked = false,
  onSelect,
}: {
  title: string;
  products: { id: string | null; name: string; revenue: number }[];
  ranked?: boolean;
  onSelect: (p: { id: string | null; name: string }) => void;
}) {
  const max = Math.max(1, ...products.map((p) => p.revenue));
  return (
    <IosSection title={title}>
      <IosGroup className="pb-[14px] pt-2">
        {products.length === 0 && <div className="px-4 py-3 text-[15px] text-muted">Aucune vente sur la période.</div>}
        {products.map((p, i) => (
          <IosRow
            key={p.id ?? p.name}
            onClick={() => onSelect(p)}
            className="min-h-[52px]"
            left={ranked ? <Rank n={i + 1} /> : undefined}
            right={
              <span className="flex-shrink-0 text-right text-[15px] font-semibold tabular">{eur(p.revenue)}</span>
            }
          >
            <span className="block truncate text-[16px]">{p.name}</span>
            <IosBar pct={(p.revenue / max) * 100} color="bg-product-bar" />
          </IosRow>
        ))}
      </IosGroup>
    </IosSection>
  );
}

/** « Position vs réseau » : barre de la boutique et repère de la moyenne (anonymisée) du périmètre. */
export function PositionGroup({ items, note = "Repère : moyenne réseau (anonymisée)" }: { items: PositionIndicatorDTO[]; note?: string }) {
  return (
    <IosSection title="Position vs réseau">
      <IosGroup className="px-4 py-1">
        {items.map((pi) => (
          <div key={pi.label} className="border-b-[0.5px] border-ios-sep py-[11px]">
            <div className="flex justify-between gap-2 text-[15px]">
              <span className="min-w-0 truncate">{pi.label}</span>
              <span className="flex-shrink-0 font-semibold tabular">{pi.value == null ? "—" : pi.unit === "eur" ? eur(pi.value) : margin(pi.value)}</span>
            </div>
            <div className="relative mt-[7px] h-[5px] rounded-[3px] bg-ios-fill">
              <div className="h-full rounded-[3px] bg-accent" style={{ width: `${Math.max(0, Math.min(100, pi.pct))}%` }} />
              <div className="absolute -top-[3px] h-[11px] w-[2px] rounded-[1px] bg-muted-2" style={{ left: `${Math.max(0, Math.min(100, pi.avgPct))}%` }} />
            </div>
          </div>
        ))}
        <div className="pb-[6px] pt-2 text-[12px] text-muted">{note}</div>
      </IosGroup>
    </IosSection>
  );
}

/* --- Accueil mobile (handoff_v3) : cartes blanches sans séparateurs, 3 lignes puis « Voir les N ». --- */

/** Écart en pastille arrondie (« +6,2 % »), vert en hausse, corail en baisse. */
export function DeltaPill({ delta, arrow = false, className }: { delta: number | null; arrow?: boolean; className?: string }) {
  if (delta == null) return null;
  const up = delta >= 0;
  return (
    <span
      className={cx(
        "flex-shrink-0 whitespace-nowrap rounded-full font-semibold tabular",
        up ? "bg-positive-bg text-up" : "bg-danger-bg text-danger",
        className ?? "px-[6px] py-px text-[11px]",
      )}
    >
      {arrow ? (up ? "▲ " : "▼ ") : up ? "+" : "−"}
      {pct1(Math.abs(delta))}
    </span>
  );
}

/** Variation en % entre une valeur et sa référence ; null sans référence. */
export const deltaOf = (value: number, ref: number | null) => (ref != null && ref > 0 ? ((value - ref) / ref) * 100 : null);

/** Carte blanche titrée en phrase, avec « Voir les N » / « Afficher moins » au-delà de 3 lignes. */
function FoldCard<T>({
  title,
  items,
  render,
}: {
  title: ReactNode;
  items: T[];
  render: (item: T, index: number) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, 3);
  return (
    <section className="rounded-[22px] bg-white px-4 pb-2 pt-4">
      <h2 className="flex items-center gap-2 text-[20px] font-bold tracking-[-0.3px]">{title}</h2>
      <div className="mt-[6px] flex flex-col">{shown.map(render)}</div>
      {items.length > 3 && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1 flex min-h-[44px] w-full items-center justify-center border-t-[0.5px] border-ios-sep text-[15px] font-medium text-accent"
        >
          {open ? "Afficher moins" : `Voir les ${items.length}`}
        </button>
      )}
    </section>
  );
}

/**
 * Top boutiques intégrées / franchises : rang coloré selon le type, objectif,
 * CA et écart, CA de la période comparée (« mer. dernier 4 539 € »).
 */
export function TopBoutiquesCard({
  title,
  items,
  kind,
  cmpLabel,
  onSelect,
}: {
  title: string;
  items: BoutiqueCardDTO[];
  kind: "INTEGRATED" | "FRANCHISE";
  /** Période comparée en bref (« mer. dernier »). */
  cmpLabel: string;
  onSelect: (id: string) => void;
}) {
  if (items.length === 0) return null;
  const fra = kind === "FRANCHISE";
  return (
    <FoldCard
      title={
        <>
          <span className={cx("h-[10px] w-[10px] rounded-full", fra ? "bg-franchise-vivid" : "bg-integrated")} />
          {title}
        </>
      }
      items={items}
      render={(b, i) => (
        <button key={b.id} type="button" onClick={() => onSelect(b.id)} className="flex items-center gap-3 py-[9px] text-left">
          <span
            className={cx(
              "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] text-[13px] font-bold tabular",
              fra ? "bg-franchise-bg text-franchise-ink" : "bg-positive-bg text-integrated-ink",
            )}
          >
            {i + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-[5px]">
              <span className="truncate text-[16px] font-semibold">{b.city}</span>
              {!b.synced && <span title="Non synchronisée" aria-label="Non synchronisée" className="h-[6px] w-[6px] flex-shrink-0 rounded-full bg-warn" />}
            </span>
            {b.objectivePct != null && <span className="block text-[12px] text-muted-2">{Math.round(b.objectivePct)} % de l'objectif</span>}
          </span>
          <span className="flex-shrink-0 text-right">
            <span className="flex items-baseline justify-end gap-[6px]">
              <span className="text-[16px] font-bold tabular">{eur(b.ca)}</span>
              <DeltaPill delta={b.delta} />
            </span>
            {b.prevCa != null && (
              <span className="mt-[2px] block text-[12px] tabular text-muted-2">
                {cmpLabel} {eur(b.prevCa)}
              </span>
            )}
          </span>
        </button>
      )}
    />
  );
}

/**
 * Top produits classés par CA : CA et écart, quantité vendue et CA de la
 * période comparée en petit, barre à l'échelle du n° 1. La quantité reste
 * sans unité : une partie du catalogue se vend au poids.
 */
export function TopProductsCard({
  products,
  cmpLabel,
  onSelect,
}: {
  products: ProductRankDTO[];
  cmpLabel: string;
  onSelect: (p: { id: string | null; name: string }) => void;
}) {
  const max = Math.max(1, ...products.map((p) => p.revenue));
  if (products.length === 0) {
    return (
      <section className="rounded-[22px] bg-white px-4 py-4">
        <h2 className="text-[20px] font-bold tracking-[-0.3px]">Top produits</h2>
        <div className="mt-2 text-[15px] text-muted">Aucune vente sur la période.</div>
      </section>
    );
  }
  return (
    <FoldCard
      title="Top produits"
      items={products}
      render={(p, i) => (
        <button key={p.id ?? p.name} type="button" onClick={() => onSelect(p)} className="block w-full py-[9px] text-left">
          <span className="flex justify-between gap-2 text-[15px]">
            <span className="min-w-0 truncate">
              <span className="mr-2 font-semibold text-faint">{i + 1}</span>
              {p.name}
            </span>
            <span className="flex flex-shrink-0 items-baseline gap-[6px]">
              <span className="font-bold tabular">{eur(p.revenue)}</span>
              <DeltaPill delta={deltaOf(p.revenue, p.prevRevenue)} />
            </span>
          </span>
          <span className="mt-[2px] flex justify-between gap-2 text-[12px] tabular text-muted-2">
            <span>{num(p.quantity)} vendus</span>
            {p.prevRevenue != null && (
              <span>
                {cmpLabel} {eur(p.prevRevenue)}
              </span>
            )}
          </span>
          <span className="mt-[7px] block h-[6px] rounded-[3px] bg-soft-fill">
            <span className="block h-full rounded-[3px] bg-product-bar" style={{ width: `${(p.revenue / max) * 100}%` }} />
          </span>
        </button>
      )}
    />
  );
}

/** « Position vs réseau » de la page boutique : barre verte et trait foncé = moyenne. */
export function PositionCard({ items, note = "Trait foncé : moyenne réseau" }: { items: PositionIndicatorDTO[]; note?: string }) {
  return (
    <section className="rounded-[22px] bg-white px-4 pb-[10px] pt-4">
      <h2 className="text-[20px] font-bold tracking-[-0.3px]">Position vs réseau</h2>
      <div className="mt-2">
        {items.map((pi) => (
          <div key={pi.label} className="py-[9px]">
            <div className="flex justify-between gap-2 text-[15px]">
              <span className="min-w-0 truncate">{pi.label}</span>
              <span className="flex-shrink-0 font-semibold tabular">{pi.value == null ? "—" : pi.unit === "eur" ? eur(pi.value) : margin(pi.value)}</span>
            </div>
            <div className="relative mt-2 h-[6px] rounded-[3px] bg-soft-fill">
              <div className="h-full rounded-[3px] bg-integrated" style={{ width: `${Math.max(0, Math.min(100, pi.pct))}%` }} />
              <div className="absolute -top-1 h-[14px] w-[3px] rounded-[2px] bg-[oklch(0.35_0.02_150)]" style={{ left: `${Math.max(0, Math.min(100, pi.avgPct))}%` }} />
            </div>
          </div>
        ))}
        <div className="pb-[6px] pt-1 text-[12px] text-muted-2">{note}</div>
      </div>
    </section>
  );
}
